import { useState } from 'react';
import type { BackupSchedule } from '@/mocks/saGlobalBackupCenter';

interface BackupScheduleModalProps {
  schedule: BackupSchedule | null;
  isDuplicating: boolean;
  onClose: () => void;
  onSave: (data: Partial<BackupSchedule>) => void;
}

const frequencyOptions: { value: string; label: string }[] = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'custom', label: 'Personalizado' },
];

const serverOptions = ['us-east-1', 'us-west-2', 'eu-west-1', 'sa-east-1', 'ap-southeast-1'];

const databaseOptions = ['produccion_principal', 'analytics_db', 'logs_db', 'usuarios_db', 'configuracion_db', 'marketplace_db', 'todas'];

export default function BackupScheduleModal({ schedule, isDuplicating, onClose, onSave }: BackupScheduleModalProps) {
  const [name, setName] = useState(isDuplicating ? `${schedule?.name || ''} (Copia)` : schedule?.name || '');
  const [frequency, setFrequency] = useState(schedule?.frequency || 'daily');
  const [time, setTime] = useState(schedule?.time || '04:00 UTC');
  const [server, setServer] = useState(schedule?.server || 'us-east-1');
  const [database, setDatabase] = useState(schedule?.database || 'produccion_principal');
  const [retentionDays, setRetentionDays] = useState(schedule?.retentionDays || 30);
  const [enabled, setEnabled] = useState(isDuplicating ? true : schedule?.enabled ?? true);
  const [description, setDescription] = useState(schedule?.description || '');

  const title = isDuplicating ? 'Duplicar Programación' : schedule ? 'Editar Programación' : 'Nueva Programación';

  const handleSave = () => {
    onSave({
      name,
      frequency: frequency as BackupSchedule['frequency'],
      time,
      server,
      database,
      retentionDays,
      enabled,
      description,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-background-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className={`ri-${isDuplicating ? 'file-copy-line' : schedule ? 'edit-line' : 'add-circle-line'} text-foreground-600 text-lg`}></i>
            <h3 className="text-foreground-900 font-bold text-sm">{title}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Nombre</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre de la programación"
              className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs placeholder:text-foreground-400 outline-none focus:border-background-400 transition-colors"
            />
          </div>

          {/* Frequency + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Frecuencia</label>
              <div className="relative">
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="appearance-none w-full pl-3 pr-8 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs font-medium outline-none focus:border-background-400 cursor-pointer transition-colors"
                >
                  {frequencyOptions.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Hora</label>
              <input
                type="text"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="04:00 UTC"
                className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs placeholder:text-foreground-400 outline-none focus:border-background-400 transition-colors"
              />
            </div>
          </div>

          {/* Server + Database */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Servidor</label>
              <div className="relative">
                <select
                  value={server}
                  onChange={(e) => setServer(e.target.value)}
                  className="appearance-none w-full pl-3 pr-8 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs font-medium outline-none focus:border-background-400 cursor-pointer transition-colors"
                >
                  {serverOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Base de Datos</label>
              <div className="relative">
                <select
                  value={database}
                  onChange={(e) => setDatabase(e.target.value)}
                  className="appearance-none w-full pl-3 pr-8 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs font-medium outline-none focus:border-background-400 cursor-pointer transition-colors"
                >
                  {databaseOptions.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              </div>
            </div>
          </div>

          {/* Retention */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Retención (días)</label>
            <input
              type="number"
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value))}
              min={1}
              max={365}
              className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs outline-none focus:border-background-400 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Descripción de la programación..."
              className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs placeholder:text-foreground-400 outline-none focus:border-background-400 transition-colors resize-none"
            />
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center gap-3 py-2">
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabled ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
            </button>
            <span className="text-foreground-700 text-xs font-semibold">{enabled ? 'Activada' : 'Desactivada'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 px-5 py-4 rounded-b-2xl flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="flex-1 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-check-line text-sm mr-1"></i>
            {isDuplicating ? 'Duplicar' : schedule ? 'Guardar Cambios' : 'Crear Programación'}
          </button>
        </div>
      </div>
    </div>
  );
}